import React, { useState, useEffect } from 'react';
import { prioritySkills as prioritySkillsApi } from '../../api';
import { findNodeTitlePath } from '../SkillTree/data';
import './PrioritySkillsSection.css';

const formatDate = (d) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

// Deadlines are stored as a date; treat the whole due day as still open.
const endOfDay = (d) => {
  const date = new Date(d);
  date.setHours(23, 59, 59, 999);
  return date;
};

function PrioritySkillsSection({ playerId, user }) {
  const [prioritySkills, setPrioritySkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const effectivePlayerId = playerId || user?.id || user?._id;
    if (effectivePlayerId) {
      loadPrioritySkills();
    }
  }, [playerId, user?.id, user?._id]);

  const loadPrioritySkills = async () => {
    try {
      setLoading(true);
      const response = await prioritySkillsApi.getMyPriorities();

      if (response.data && response.data.prioritySkill) {
        const skillData = response.data.prioritySkill;

        // Transform data for display. Resolve the full branch path from the
        // skill tree itself rather than trusting skillData.skillNames -
        // older priority assignments may only have a bare leaf name (or, for
        // ones created before this fix, a raw "Skill <id>" placeholder), and
        // leaf names alone are ambiguous (many different branches have a
        // leaf literally called "Depth" or "Power").
        const skills = skillData.skillIds.map((skillId, index) => {
          const skillIdStr = skillId.toString();
          const path = findNodeTitlePath(skillIdStr);
          const trimmedPath = path && path[0] === 'Technique' ? path.slice(1) : path;
          const leafName = trimmedPath ? trimmedPath[trimmedPath.length - 1] : (skillData.skillNames[index] || 'Unknown Skill');
          const branchPath = trimmedPath && trimmedPath.length > 1 ? trimmedPath.slice(0, -1).join(' › ') : '';
          const isUnlocked = skillData.unlockedStatus && skillData.unlockedStatus[skillIdStr];
          const deadline = skillData.deadlineTargets && skillData.deadlineTargets[skillIdStr];

          return {
            skillId,
            skillName: leafName,
            branchPath,
            isUnlocked: !!isUnlocked,
            unlockedAt: isUnlocked ? isUnlocked.unlockedAt : null,
            deadline,
            // Failed once the due day has fully passed without completion
            isDueOverdue: !!deadline && !isUnlocked && endOfDay(deadline) < new Date()
          };
        });

        setPrioritySkills(skills);
        setError('');
      } else {
        setPrioritySkills([]);
      }
    } catch (err) {
      console.error('❌ Error loading priority skills:', err);
      setError('Failed to load priority skills');
      setPrioritySkills([]);
    } finally {
      setLoading(false);
    }
  };

  const unlockedCount = prioritySkills.filter(s => s.isUnlocked).length;
  const totalCount = prioritySkills.length;
  const progressPercent = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

  return (
    <div className="priority-skills-section">
      {/* Title - No longer clickable, controlled by parent stat box */}
      <div className="priority-header priority-header--static">
        <span>⭐ Priority Skills ({unlockedCount}/{totalCount})</span>
      </div>

      {loading && (
        <div className="loading-state">Loading priority skills...</div>
      )}

      {error && (
        <div className="error-state">{error}</div>
      )}

      {!loading && !error && prioritySkills.length === 0 && (
        <div className="loading-state">
          📋 No priority skills assigned yet.<br />
          Your coach can set focus skills from their dashboard.
        </div>
      )}

      {!loading && !error && prioritySkills.length > 0 && (
        <>
          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-bar-outer">
              <div
                className="progress-bar-inner"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="progress-text">
              {unlockedCount} of {totalCount} unlocked • {progressPercent}%
            </div>
          </div>

          <div className="skills-list">
            {prioritySkills.map((skill) => (
              <div
                key={skill.skillId}
                className={`skill-item ${skill.isUnlocked ? 'unlocked' : skill.isDueOverdue ? 'failed' : 'locked'}`}
              >
                <div className="skill-info">
                  <span className="skill-icon">
                    {skill.isUnlocked ? '✅' : skill.isDueOverdue ? '❌' : '🔒'}
                  </span>
                  <span className="skill-name-group">
                    {skill.branchPath && (
                      <span className="skill-branch-path">{skill.branchPath}</span>
                    )}
                    <span className="skill-name">{skill.skillName}</span>
                  </span>
                  {skill.isUnlocked && <span className="status-badge status-badge--success">Successful</span>}
                  {skill.isDueOverdue && <span className="status-badge status-badge--failed">Failed</span>}
                </div>
                {skill.isUnlocked && skill.unlockedAt && (
                  <div className="skill-deadline skill-deadline--success">
                    Completed: {formatDate(skill.unlockedAt)}
                    {skill.deadline && ` • Due: ${formatDate(skill.deadline)}`}
                  </div>
                )}
                {!skill.isUnlocked && skill.deadline && (
                  <div className={`skill-deadline ${skill.isDueOverdue ? 'skill-deadline--failed' : ''}`}>
                    {skill.isDueOverdue ? 'Expired' : 'Due'}: {formatDate(skill.deadline)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default PrioritySkillsSection;
